// What the web import tells a person, whatever the server (or the platform in
// front of it) actually answered.
//
// /api/self/import-web is one synchronous request that can run for minutes, and
// the body it replies with is JSON only when OUR code replied. A body over the
// platform's limit comes back as an HTML 413 page, a run past its time limit as
// an HTML 504, an overloaded edge as a plain-text 502. The old client called
// `res.json()` on all of them and printed what threw: "Unexpected token '<',
// '<!DOCTYPE'… is not valid JSON" in the middle of someone's import. Nothing
// here ever lets a parse error, a status code or a stack of platform HTML reach
// the screen. Every outcome is a sentence in the house voice.
//
// Pure but for the fetch, which is injectable.

import { NETWORK_LINE, isNetworkError } from '../auth-errors'

/** The paste lane's ceiling. The server refuses more than this (413); the client
 *  says so BEFORE sending, so nobody waits through an upload to be told. */
export const PASTE_CAP = 200_000

/** 413: the body was more than could be taken in at once. */
export const TOO_MUCH_LINE = 'that was too much to bring in at once. please try a smaller piece.'

/** The paste box, over the cap, before anything is sent. */
export const OVER_CAP_LINE = `that is more than can be brought in at once (${PASTE_CAP.toLocaleString('en-US')} characters at most). please paste a smaller piece, or use the 3-part prompt.`

const GENERIC_LINE = "that didn't go through. please try again."

export interface ImportResult {
  facts_written: number
  questions_written: number
  summary: string
  source: string
  /** moving in (lib/intake): the run this import was kept as, for "what moved in" */
  run?: string | null
}

export type ImportOutcome =
  | { ok: true; data: ImportResult }
  | { ok: false; line: string; cause: 'too_large' | 'network' | 'rate_limited' | 'signed_out' | 'refused' | 'unreadable' }

/** The paste text, measured the way the server measures it (trimmed). */
export function pasteLength(text: string): number {
  return text.trim().length
}
export function overPasteCap(text: string): boolean {
  return pasteLength(text) > PASTE_CAP
}

/** A short, human sentence the server itself sent, if it sent one and it is fit to show. */
function serverSaid(body: unknown): string | null {
  const said = body && typeof body === 'object' ? (body as { error?: unknown }).error : null
  if (typeof said !== 'string') return null
  const line = said.trim()
  if (!line || line.length > 140) return null
  // developer text and status-code talk are not for the person at the door
  if (/\b(supabase|stack|undefined|null|exception|ECONN|500|502|503|504)\b|[{}<>]/i.test(line)) return null
  return line.replace(/^([A-Z])(?=[a-z])/, (c) => c.toLowerCase())
}

/** POST an import and come back with an answer a person can read, always. */
export async function postImport(
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch = fetch,
): Promise<ImportOutcome> {
  let res: Response
  try {
    res = await fetchImpl('/api/self/import-web', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    })
  } catch (err) {
    // the request never got an answer: offline, a dropped connection, a timeout
    const raw = err instanceof Error ? err.message : ''
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false
    const aborted = err instanceof Error && (err.name === 'AbortError' || err.name === 'TimeoutError')
    if (offline || aborted || isNetworkError(raw)) return { ok: false, line: NETWORK_LINE, cause: 'network' }
    return { ok: false, line: GENERIC_LINE, cause: 'unreadable' }
  }

  // the body, read as text first: it is JSON only when our own code replied
  let text = ''
  try {
    text = await res.text()
  } catch {
    // the connection died mid-body: the same as no answer at all
    return { ok: false, line: NETWORK_LINE, cause: 'network' }
  }
  let body: unknown = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = null
  }

  if (res.ok) {
    const d = body as Partial<ImportResult> | null
    if (d && typeof d === 'object' && typeof d.facts_written === 'number') {
      return {
        ok: true,
        data: {
          facts_written: d.facts_written,
          questions_written: typeof d.questions_written === 'number' ? d.questions_written : 0,
          summary: typeof d.summary === 'string' ? d.summary : '',
          source: typeof d.source === 'string' ? d.source : '',
          ...(typeof d.run === 'string' && d.run ? { run: d.run } : {}),
        },
      }
    }
    // a 2xx that is not our answer (a captive portal, a proxy page)
    return { ok: false, line: GENERIC_LINE, cause: 'unreadable' }
  }

  if (res.status === 413) return { ok: false, line: TOO_MUCH_LINE, cause: 'too_large' }
  if (res.status === 401 || res.status === 403) {
    return { ok: false, line: 'please sign in again, then bring it in once more.', cause: 'signed_out' }
  }
  if (res.status === 429) {
    return { ok: false, line: 'that is a lot of imports in a short while. please wait a little, then try again.', cause: 'rate_limited' }
  }
  // the platform's own errors (a timeout, an overloaded edge): it is the connection to us
  if (res.status >= 500 || res.status === 408) return { ok: false, line: NETWORK_LINE, cause: 'network' }

  // any other refusal: our own sentence if the server sent a fit one
  return { ok: false, line: serverSaid(body) ?? GENERIC_LINE, cause: 'refused' }
}
